import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, Trash, FloppyDisk, Receipt, ChartLine, Calculator, Tag, Spinner,
  FilePdf, CaretDown
} from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator
} from '../../components/ui/dropdown-menu';
import { useCurrency } from '../../contexts/CompanyContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  materials: 'Materials',
  labor: 'Labor',
  subcontract: 'Subcontract',
  equipment: 'Equipment',
  transport: 'Transport',
  overhead: 'Overhead',
  contingency: 'Contingency',
  other: 'Other',
};

const CATEGORY_COLORS = {
  materials: 'border-blue-500/40 bg-blue-500/5',
  labor: 'border-emerald-500/40 bg-emerald-500/5',
  subcontract: 'border-violet-500/40 bg-violet-500/5',
  equipment: 'border-amber-500/40 bg-amber-500/5',
  transport: 'border-cyan-500/40 bg-cyan-500/5',
  overhead: 'border-rose-500/40 bg-rose-500/5',
  contingency: 'border-orange-500/40 bg-orange-500/5',
  other: 'border-zinc-500/40 bg-zinc-500/5',
};

const num = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
};

export default function QuotationCostingPage() {
  const { quotationId } = useParams();
  const navigate = useNavigate();
  const { formatCurrency } = useCurrency();

  const [costing, setCosting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchCosting = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/quotations/${quotationId}/costing`);
      setCosting(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load costing sheet');
    } finally {
      setLoading(false);
    }
  }, [quotationId]);

  useEffect(() => { fetchCosting(); }, [fetchCosting]);

  // ---- Computed totals (live preview) ----
  const totals = useMemo(() => {
    if (!costing) return { line: 0, cat: 0, total: 0, profit: 0, margin: 0 };
    const line = (costing.line_costs || []).reduce(
      (sum, lc) => sum + num(lc.quantity) * num(lc.unit_cost), 0
    );
    const cat = (costing.category_costs || []).reduce(
      (sum, sec) => sum + (sec.items || []).reduce((s, it) => s + num(it.amount), 0), 0
    );
    const total = line + cat;
    const qv = num(costing.quote_value);
    const profit = qv - total;
    const margin = qv > 0 ? (profit / qv) * 100 : 0;
    return { line, cat, total, profit, margin };
  }, [costing]);

  // ---- Mutators ----
  const updateLineCost = (idx, field, value) => {
    setCosting((c) => {
      const lines = [...(c.line_costs || [])];
      lines[idx] = { ...lines[idx], [field]: value };
      return { ...c, line_costs: lines };
    });
  };

  const addCategoryItem = (catIdx) => {
    setCosting((c) => {
      const cats = [...(c.category_costs || [])];
      const items = [...(cats[catIdx].items || []), { name: '', amount: 0, notes: '' }];
      cats[catIdx] = { ...cats[catIdx], items };
      return { ...c, category_costs: cats };
    });
  };

  const updateCategoryItem = (catIdx, itemIdx, field, value) => {
    setCosting((c) => {
      const cats = [...(c.category_costs || [])];
      const items = [...(cats[catIdx].items || [])];
      items[itemIdx] = { ...items[itemIdx], [field]: value };
      cats[catIdx] = { ...cats[catIdx], items };
      return { ...c, category_costs: cats };
    });
  };

  const removeCategoryItem = (catIdx, itemIdx) => {
    setCosting((c) => {
      const cats = [...(c.category_costs || [])];
      const items = (cats[catIdx].items || []).filter((_, i) => i !== itemIdx);
      cats[catIdx] = { ...cats[catIdx], items };
      return { ...c, category_costs: cats };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        line_costs: (costing.line_costs || []).map((lc) => ({
          quote_line_id: lc.quote_line_id,
          quote_line_index: lc.quote_line_index,
          description: lc.description || '',
          quantity: num(lc.quantity),
          unit_cost: num(lc.unit_cost),
          total_cost: num(lc.quantity) * num(lc.unit_cost),
          supplier: lc.supplier || '',
          notes: lc.notes || '',
        })),
        category_costs: (costing.category_costs || []).map((sec) => ({
          category: sec.category,
          items: (sec.items || []).map((it) => ({
            name: it.name || '', amount: num(it.amount), notes: it.notes || '',
          })),
          subtotal: (sec.items || []).reduce((s, it) => s + num(it.amount), 0),
        })),
        notes: costing.notes || '',
      };
      const res = await axios.put(`${API_URL}/quotations/${quotationId}/costing`, payload);
      setCosting(res.data);
      toast.success('Costing sheet saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save costing sheet');
    } finally {
      setSaving(false);
    }
  };

  const handleExportPdf = async (variant = 'internal') => {
    try {
      const res = await axios.get(
        `${API_URL}/quotations/${quotationId}/costing/pdf?variant=${variant}`,
        { responseType: 'blob' }
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${costing?.quotation_number || 'Quote'}_Costing_${variant === 'internal' ? 'INTERNAL' : 'CLIENT'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${variant === 'internal' ? 'Internal' : 'Client'} costing PDF downloaded`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to export PDF');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="costing-loading">
        <Spinner size={32} className="animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!costing) return null;

  const marginColor = totals.margin >= 25 ? 'text-emerald-600' : totals.margin >= 10 ? 'text-amber-600' : 'text-red-600';

  return (
    <div className="space-y-6" data-testid="costing-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-start gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="costing-back-btn">
            <ArrowLeft size={18} />
          </Button>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold tracking-tight">Costing Sheet</h1>
              <Badge variant="outline" className="font-mono">{costing.quotation_number}</Badge>
              {/* Revision badge — always shown so the user knows which revision this costing belongs to */}
              <Badge
                variant="secondary"
                className={`font-mono ${costing.quotation_revision === 'R0' ? '' : 'bg-violet-500/15 text-violet-700 dark:text-violet-300'}`}
                data-testid="costing-revision-badge"
              >
                {costing.quotation_revision || 'R0'}
              </Badge>
              {!costing.exists && <Badge variant="outline" className="text-amber-600 border-amber-300">Unsaved</Badge>}
            </div>
            <p className="text-muted-foreground text-sm mt-1">
              Budget &amp; P&amp;L for quotation <strong>{costing.quotation_number}</strong> revision <strong className="font-mono">{costing.quotation_revision || 'R0'}</strong>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="costing-export-btn">
                <FilePdf size={16} className="mr-1.5" /> Export PDF
                <CaretDown size={14} className="ml-1.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel>Choose variant</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => handleExportPdf('internal')} data-testid="costing-export-internal">
                <FilePdf size={14} className="mr-2 text-rose-600" />
                <div>
                  <div className="font-medium">Internal Report</div>
                  <div className="text-xs text-muted-foreground">Full P&amp;L, line costs, watermark</div>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExportPdf('client')} data-testid="costing-export-client">
                <FilePdf size={14} className="mr-2 text-emerald-600" />
                <div>
                  <div className="font-medium">Client Summary</div>
                  <div className="text-xs text-muted-foreground">Cost breakdown only, no margins</div>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={handleSave} disabled={saving} data-testid="costing-save-btn"
            className="bg-gradient-to-r from-[#0f172a] to-[#1e3a5f] hover:from-[#1e293b] hover:to-[#2d4a6f] text-white">
            {saving ? <Spinner size={16} className="animate-spin mr-2" /> : <FloppyDisk size={16} className="mr-2" />}
            {saving ? 'Saving...' : 'Save Costing'}
          </Button>
        </div>
      </div>

      {/* Summary Cards: Quote Value | Cost | Profit | Margin */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <SummaryCard icon={Receipt} label="Total Quote Value" value={formatCurrency(num(costing.quote_value))} accent="text-blue-600" testId="summary-quote-value" />
        <SummaryCard icon={Calculator} label="Total Cost" value={formatCurrency(totals.total)} accent="text-rose-600" testId="summary-total-cost" />
        <SummaryCard icon={ChartLine} label="Gross Profit" value={formatCurrency(totals.profit)} accent={totals.profit >= 0 ? 'text-emerald-600' : 'text-red-600'} testId="summary-gross-profit" />
        <SummaryCard icon={Tag} label="Margin %" value={`${totals.margin.toFixed(2)}%`} accent={marginColor} testId="summary-margin" />
      </div>

      {/* Line-Item Costing (per quote line) */}
      <div className="bg-card border rounded-xl overflow-hidden" data-testid="line-costing-section">
        <div className="px-4 sm:px-5 py-3 border-b bg-muted/30 flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-base">Line Costing</h2>
            <p className="text-muted-foreground text-xs">Cost per line item from the quotation</p>
          </div>
          <Badge variant="outline">{(costing.line_costs || []).length} lines</Badge>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/40 text-muted-foreground text-xs uppercase tracking-wide">
                <th className="text-left px-4 py-2 w-10">#</th>
                <th className="text-left px-4 py-2 min-w-[220px]">Description (from quote)</th>
                <th className="text-right px-4 py-2 w-24">Qty</th>
                <th className="text-right px-4 py-2 w-32">Unit Cost</th>
                <th className="text-right px-4 py-2 w-32">Total Cost</th>
                <th className="text-left px-4 py-2 w-44">Supplier</th>
                <th className="text-left px-4 py-2 min-w-[160px]">Notes</th>
              </tr>
            </thead>
            <tbody>
              {(costing.line_costs || []).map((lc, idx) => {
                const t = num(lc.quantity) * num(lc.unit_cost);
                return (
                  <tr key={idx} className="border-t hover:bg-muted/20" data-testid={`line-cost-row-${idx}`}>
                    <td className="px-4 py-2 text-muted-foreground">{idx + 1}</td>
                    <td className="px-4 py-2"><span className="font-medium">{lc.description || '-'}</span></td>
                    <td className="px-2 py-1.5">
                      <Input type="number" step="0.01" value={lc.quantity || 0}
                        onChange={(e) => updateLineCost(idx, 'quantity', e.target.value)}
                        className="text-right h-8" data-testid={`line-qty-${idx}`} />
                    </td>
                    <td className="px-2 py-1.5">
                      <Input type="number" step="0.01" value={lc.unit_cost || 0}
                        onChange={(e) => updateLineCost(idx, 'unit_cost', e.target.value)}
                        className="text-right h-8" data-testid={`line-unit-cost-${idx}`} />
                    </td>
                    <td className="px-4 py-2 text-right font-mono font-medium">{formatCurrency(t)}</td>
                    <td className="px-2 py-1.5">
                      <Input value={lc.supplier || ''}
                        onChange={(e) => updateLineCost(idx, 'supplier', e.target.value)}
                        placeholder="Supplier" className="h-8" data-testid={`line-supplier-${idx}`} />
                    </td>
                    <td className="px-2 py-1.5">
                      <Input value={lc.notes || ''}
                        onChange={(e) => updateLineCost(idx, 'notes', e.target.value)}
                        placeholder="Notes" className="h-8" data-testid={`line-notes-${idx}`} />
                    </td>
                  </tr>
                );
              })}
              {(costing.line_costs || []).length === 0 && (
                <tr><td colSpan={7} className="text-center py-8 text-muted-foreground">No line items in this quotation.</td></tr>
              )}
            </tbody>
            {(costing.line_costs || []).length > 0 && (
              <tfoot>
                <tr className="border-t bg-muted/40 font-semibold">
                  <td colSpan={4} className="px-4 py-2 text-right">Line Costs Subtotal</td>
                  <td className="px-4 py-2 text-right font-mono" data-testid="line-costs-subtotal">{formatCurrency(totals.line)}</td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Category-Based Costing */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="category-costing-section">
        {(costing.category_costs || []).map((sec, catIdx) => {
          const subtotal = (sec.items || []).reduce((s, it) => s + num(it.amount), 0);
          return (
            <div key={sec.category} className={`rounded-xl border-2 ${CATEGORY_COLORS[sec.category] || 'border-border'} overflow-hidden`}
              data-testid={`category-${sec.category}`}>
              <div className="px-4 py-3 border-b border-current/20 flex items-center justify-between">
                <div>
                  <h3 className="font-semibold">{CATEGORY_LABELS[sec.category] || sec.category}</h3>
                  <p className="text-xs text-muted-foreground">{(sec.items || []).length} item{(sec.items || []).length !== 1 ? 's' : ''}</p>
                </div>
                <div className="text-right">
                  <div className="text-xs text-muted-foreground">Subtotal</div>
                  <div className="font-mono font-bold" data-testid={`category-${sec.category}-subtotal`}>{formatCurrency(subtotal)}</div>
                </div>
              </div>
              <div className="p-3 space-y-2">
                {(sec.items || []).map((item, itemIdx) => (
                  <div key={itemIdx} className="grid grid-cols-12 gap-1.5 items-center" data-testid={`category-${sec.category}-item-${itemIdx}`}>
                    <Input className="col-span-5 h-8 text-sm" placeholder="Item name"
                      value={item.name}
                      onChange={(e) => updateCategoryItem(catIdx, itemIdx, 'name', e.target.value)} />
                    <Input className="col-span-3 h-8 text-sm text-right" type="number" step="0.01" placeholder="Amount"
                      value={item.amount || 0}
                      onChange={(e) => updateCategoryItem(catIdx, itemIdx, 'amount', e.target.value)} />
                    <Input className="col-span-3 h-8 text-sm" placeholder="Notes"
                      value={item.notes || ''}
                      onChange={(e) => updateCategoryItem(catIdx, itemIdx, 'notes', e.target.value)} />
                    <Button variant="ghost" size="sm" className="col-span-1 h-8 px-1 text-muted-foreground hover:text-red-500"
                      onClick={() => removeCategoryItem(catIdx, itemIdx)}
                      data-testid={`category-${sec.category}-remove-${itemIdx}`}>
                      <Trash size={14} />
                    </Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" className="w-full"
                  onClick={() => addCategoryItem(catIdx)}
                  data-testid={`category-${sec.category}-add`}>
                  <Plus size={14} className="mr-1" /> Add Item
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Notes */}
      <div className="bg-card border rounded-xl p-4" data-testid="costing-notes-section">
        <label className="text-sm font-medium mb-2 block">Internal Notes</label>
        <Textarea
          value={costing.notes || ''}
          onChange={(e) => setCosting((c) => ({ ...c, notes: e.target.value }))}
          placeholder="Internal cost / margin notes (not shared with client)"
          rows={3}
          data-testid="costing-notes-input"
        />
      </div>

      {/* Sticky Save Bar (mobile-friendly) */}
      <div className="sticky bottom-2 z-10 flex justify-end">
        <Button onClick={handleSave} disabled={saving}
          className="bg-gradient-to-r from-[#0f172a] to-[#1e3a5f] text-white shadow-lg"
          data-testid="costing-save-bottom-btn">
          {saving ? <Spinner size={16} className="animate-spin mr-2" /> : <FloppyDisk size={16} className="mr-2" />}
          Save Costing Sheet
        </Button>
      </div>
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, accent, testId }) {
  return (
    <div className="bg-card border rounded-xl p-4 flex items-center gap-3" data-testid={testId}>
      <div className={`w-10 h-10 rounded-lg bg-muted flex items-center justify-center ${accent}`}>
        <Icon size={20} weight="fill" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className={`text-xl font-bold font-mono truncate ${accent}`}>{value}</div>
      </div>
    </div>
  );
}
